import React, { useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import useWebRTC from '../hooks/useWebRTC';
import DirectVideoStream from './DirectVideoStream';
import { getBrowserBackendUrl, getSocketConfig } from '../utils/config';

const PmsView = () => {
  const [sessionId, setSessionId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [targetUrl, setTargetUrl] = useState('https://www.google.com');
  const [socket, setSocket] = useState(null);
  const [isStreamReady, setStreamReady] = useState(false);

  const {
    isConnecting,
    disconnect: disconnectWebRTC,
    sendUserInput,
    attachVideo,
    isConnected
  } = useWebRTC(sessionId, () => setStreamReady(true));

  const sendClick = useCallback((x, y, button = 'left', videoDimensions) => {
    sendUserInput(socket, { type: 'click', x, y, button, videoDimensions });
  }, [socket, sendUserInput]);

  const sendScroll = useCallback((x, y, deltaX, deltaY, videoDimensions) => {
    sendUserInput(socket, { type: 'scroll', x, y, deltaX, deltaY, videoDimensions });
  }, [socket, sendUserInput]);

  const sendKeyboard = useCallback((key, modifiers = {}) => {
    sendUserInput(socket, { type: 'keyboard', key, modifiers });
  }, [socket, sendUserInput]);

  const disconnectAndReset = useCallback(async () => {
    console.log('[PmsView] Disconnecting session...');
    
    // 1. Tell the backend to terminate the session pod
    if (sessionId) {
      try {
        const browserBackendUrl = getBrowserBackendUrl();
        // We don't need to wait for this, just fire and forget.
        fetch(`${browserBackendUrl}/orchestrator/sessions/${sessionId}`, {
          method: 'DELETE',
        });
      } catch (error) {
        console.error('[PmsView] Failed to send termination request to backend:', error);
      }
    }

    // 2. Disconnect local clients
    disconnectWebRTC();
    if (socket) socket.disconnect();

    // 3. Reset all local state to return to the entry page
    setSocket(null);
    setSessionId(null);
    setStreamReady(false); // <-- Important: reset stream ready state
    setErrorMessage('');
  }, [socket, sessionId, disconnectWebRTC]);

  const createSession = async () => {
    if (!targetUrl) {
      setErrorMessage('Please enter a target URL.');
      return;
    }
    
    setErrorMessage('');
    
    try {
      const browserBackendUrl = getBrowserBackendUrl();
      const response = await fetch(`${browserBackendUrl}/sessions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUrl }),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      
      const newSocket = io(browserBackendUrl, getSocketConfig());
      
      newSocket.on('connect', () => {
        newSocket.emit('join-session', { sessionId: data.sessionId, clientType: 'react-client' });
        setSocket(newSocket);
        setSessionId(data.sessionId);
      });

      newSocket.on('session-error', (data) => {
        setErrorMessage(data.error);
        disconnectAndReset();
      });
      newSocket.on('session-ended', () => disconnectAndReset());
      newSocket.on('connect_error', (error) => {
        setErrorMessage(`Connection failed: ${error.message}`);
        disconnectAndReset();
      });
    } catch (error) {
      console.error('[PmsView] Error creating session:', error);
      setErrorMessage(`Failed to create session: ${error.message}`);
    }
  };

  // Single, unified render return.
  return (
    <div className="w-screen h-screen">
      {isStreamReady ? (
        // --- STREAMING VIEW ---
        <div className="relative w-full h-full bg-black">
          <main className="w-full h-full">
            <DirectVideoStream
              sessionId={sessionId}
              attachVideo={attachVideo}
              sendClick={sendClick}
              sendScroll={sendScroll}
              sendKeyboard={sendKeyboard}
              isConnected={isConnected}
            />
          </main>
          <header className="absolute top-0 right-0 p-4 z-20">
            <button
              onClick={disconnectAndReset}
              className="px-4 py-1.5 font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-red-500"
            >
              Disconnect
            </button>
          </header>
        </div>
      ) : (
        // --- ENTRY / LOADING VIEW ---
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="w-full max-w-md p-8 space-y-6 text-center">
            {sessionId ? (
              // Show loading only if we have a session but aren't connected yet
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Starting Remote Session...</h1>
                <p className="mt-2 text-md text-gray-600">Please wait while we establish a secure connection.</p>
                <p className="text-sm text-gray-500 mt-2">Session ID: {sessionId}</p>
              </div>
            ) : (
              // Show the entry form if no session exists
              <>
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900">Get Talky Agent</h1>
                  <p className="mt-2 text-md text-gray-600">Start a new secure browser session.</p>
                </div>
                <div className="space-y-4">
                  <input
                    type="url"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={createSession}
                    disabled={!targetUrl || isConnecting}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Navigate to Web Page
                  </button>
                </div>
              </>
            )}
            {errorMessage && <p className="mt-2 text-center text-sm text-red-600">{errorMessage}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default PmsView;
